import * as React from "react";
import { EmptyState } from "./EmptyState";

type TableProps = React.TableHTMLAttributes<HTMLTableElement>;
type SectionProps = React.HTMLAttributes<HTMLTableSectionElement>;
type RowOwnProps = { interactive?: boolean };
export type TableRowProps = RowOwnProps & React.HTMLAttributes<HTMLTableRowElement>;
type CellProps = React.TdHTMLAttributes<HTMLTableCellElement>;
type HeaderProps = React.ThHTMLAttributes<HTMLTableCellElement>;

export interface TableEmptyProps {
  colSpan: number;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

function cx(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(" ");
}

/**
 * TableRoot wraps the <table> in an `overflow-x-auto` div so wide tables
 * scroll horizontally on narrow viewports instead of pushing the page
 * layout into a horizontal scroll. Set `unscrollable` for the rare case
 * where a parent already provides its own scroll container or the table
 * is guaranteed to fit.
 */
type TableRootProps = TableProps & { unscrollable?: boolean };

function TableRoot({ className, unscrollable, ...rest }: TableRootProps) {
  const table = (
    <table
      className={cx("w-full border-collapse text-left", className)}
      {...rest}
    />
  );
  if (unscrollable) return table;
  return <div className="overflow-x-auto">{table}</div>;
}

function TableHead({ className, ...rest }: SectionProps) {
  return (
    <thead
      className={cx("border-b border-border bg-muted/50", className)}
      {...rest}
    />
  );
}

function TableBody({ className, ...rest }: SectionProps) {
  return (
    <tbody className={cx("divide-y divide-border", className)} {...rest} />
  );
}

function TableRow({ interactive = true, className, ...rest }: TableRowProps) {
  const hoverClass = interactive ? "hover:bg-muted/50" : "hover:bg-transparent";
  return <tr className={cx(hoverClass, className)} {...rest} />;
}

function TableHeader({ className, ...rest }: HeaderProps) {
  return (
    <th
      className={cx(
        "px-3 py-2 text-xs font-medium text-muted-foreground",
        className,
      )}
      {...rest}
    />
  );
}

function TableCell({ className, ...rest }: CellProps) {
  return (
    <td
      className={cx("px-3 py-2 text-sm text-foreground", className)}
      {...rest}
    />
  );
}

function TableEmpty({ colSpan, title, description, icon, action }: TableEmptyProps) {
  return (
    <TableRow interactive={false}>
      <TableCell colSpan={colSpan} className="p-0">
        <EmptyState title={title} description={description} icon={icon} action={action} />
      </TableCell>
    </TableRow>
  );
}

export const Table = Object.assign(TableRoot, {
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  Header: TableHeader,
  Cell: TableCell,
  Empty: TableEmpty,
});
